import { Todo, Planner, TodoWithDetails } from '@/common/types/databaseTypes';
import { Group, TextInput, Stack, Text, Divider } from '@mantine/core';
import DueDateBadge from '../DueDateBadge';
import GoalSelector from '../GoalSelector';
import TodoPlannerList from './TodoPlannerList';
import { useUpdateTodo } from '../../services/useTodoQueries';
import { useAuthStore } from '@/stores/authStore';
import { useState } from 'react';
import { GoalVisibilityFilter } from '@/common/constants/filter';
import TodoCheckBox from '../TodoCheckBox';
import { useTranslation } from 'react-i18next';

interface TodoBasicInfoProps {
    todo: Todo;
    size?: 'sm' | 'md';
    variant?: 'default' | 'unstyled';
    autoFocus?: boolean;
    onTitleChange?: (title: string) => void;
    disabled?: boolean;
}

// 체크박스와 제목 입력 필드를 포함하는 공통 컴포넌트
export const TodoBasicInfo = ({
    todo,
    size = 'sm',
    variant = 'default',
    autoFocus = false,
    onTitleChange,
    disabled = false,
}: TodoBasicInfoProps) => {
    const { t } = useTranslation(['common', 'todo']);
    const updateTodo = useUpdateTodo();
    const { user } = useAuthStore();
    const [title, setTitle] = useState(todo?.title || '');

    const handleUpdateTodo = (updates: Partial<Todo>) => {
        if (!todo?.id) return;

        updateTodo.mutate({
            id: todo?.id ?? '',
            ...updates,
            update_id: user?.id ?? '',
            update_date: new Date().toISOString(),
        });
    };

    return (
        <Group gap="xs" align="center">
            <TodoCheckBox
                todo={todo}
                onComplete={checked =>
                    handleUpdateTodo({
                        complete_date: checked
                            ? new Date().toISOString()
                            : null,
                    })
                }
            />

            <TextInput
                flex={1}
                size={size}
                variant={variant}
                autoFocus={autoFocus}
                placeholder={t('todo:component.todoDrawer.content.todo_title_placeholder')}
                value={title}
                disabled={disabled}
                onBlur={() => {
                    if (title === todo?.title) return;
                    handleUpdateTodo({ title });
                }}
                onChange={e => {
                    const newTitle = e.target.value;
                    setTitle(newTitle);
                    // 상위 컴포넌트에 제목 변경 알림
                    if (onTitleChange) {
                        onTitleChange(newTitle);
                    }
                }}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        if (title === todo?.title) return;
                        handleUpdateTodo({ title });
                    }
                }}
            />
        </Group>
    );
};

interface TodoMetadataProps {
    todo: TodoWithDetails;
    singlePlanner?: Partial<Planner> | null;
    /**
     * 컴포넌트가 Portal 내부에 렌더링되어야 하는지 여부 - false일 경우, 하위 컴포넌트에서 무언갈 선택해도 셀렉터의 부모 컴포넌트가 닫히지 않음
     * @default true
     */
    withinPortal?: boolean;
    onPlannerSelect?: (planner: Planner) => void;
    onDueDateChange?: (dueDate: string | null) => void;
    onGoalChange?: (goalId: string | null) => void;
    isNewTodo?: boolean;
    onDeletePlannerRange: (plannerId: string, rangeToDelete: number[]) => void;
}

// 마감기한, 관련목표, 날짜/시간 정보를 포함하는 공통 컴포넌트
export const TodoMetadata = ({
    todo,
    singlePlanner,
    withinPortal,
    onPlannerSelect,
    onDueDateChange,
    onGoalChange,
    isNewTodo = false,
    onDeletePlannerRange,
}: TodoMetadataProps) => {
    const { t } = useTranslation(['common', 'todo']);
    // 인박스, 끝내지 못한 할일, 새 투두 생성 중일 때는 날짜/시간 섹션 보여줄 필요 없음
    const hasNoDateToShow = !todo.planners && !singlePlanner;

    return (
        <Stack gap="xs">
            <Group gap="xs">
                <Text size="sm" c="gray" fw={500}>
                    {t('field.due_date')}
                </Text>
                <DueDateBadge
                    withinPortal={withinPortal}
                    todoId={todo?.id ?? ''}
                    dueDate={todo?.due_date}
                    isButton={true}
                    onDueDateChange={onDueDateChange}
                />
            </Group>
            <Group gap="xs">
                <Text size="sm" c="gray" fw={500}>
                    {t('todo:component.todoDrawer.content.related_goal')}
                </Text>
                <GoalSelector
                    withinPortal={withinPortal}
                    todoId={todo?.id ?? ''}
                    goalId={todo?.goal_id}
                    onGoalChange={onGoalChange}
                    goalVisibilityFilter={GoalVisibilityFilter.ALL}
                />
            </Group>
            {singlePlanner && <Divider my={4} />}
            {!hasNoDateToShow && (
                <Stack gap="xs">
                    <Text size="sm" c="gray" fw={500}>
                        {t('todo:component.todoDrawer.content.date_and_time')}
                    </Text>
                    <TodoPlannerList
                        todo={todo}
                        singlePlanner={singlePlanner}
                        isNewTodo={isNewTodo}
                        onPlannerSelect={onPlannerSelect}
                        onDeletePlannerRange={onDeletePlannerRange}
                    />
                </Stack>
            )}
        </Stack>
    );
};
